import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useCloseConfirmDialog, useConfirmDialogState } from "@/hooks/dialogs";
import { cn } from "@/lib/utils";
import { useRef } from "react";
import { buttonVariants } from "./ui/button";
import { useOnClickOutside } from "usehooks-ts";


export default function ConfirmDialog() {
    const modalRef = useRef(null);
    const dialogState = useConfirmDialogState();
    const closeDialog = useCloseConfirmDialog();

    const handleCancel = () => {
        if (!dialogState.isOpen) return;
        closeDialog();
        if (dialogState.onCancel) {
            dialogState.onCancel();
        }
    }

    const handleConfirm = () => {
        if (!dialogState.isOpen) return;
        closeDialog();
        dialogState.onConfirm();
    }

    const handleConfirmKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
        e.preventDefault();
        if (e.key === 'Enter' || e.key === 'NumpadEnter') {
            handleConfirm();
        } else if (e.key === 'Backspace' || e.key === 'Escape') {
            handleCancel();
        }
    }

    const handleOpenClose = (newOpenState: boolean) => {
        if (!newOpenState) {
            handleCancel();
        }
    }

    useOnClickOutside(modalRef, handleCancel);

    const confirmVariant = dialogState.confirmBtnVariant ?? 'destructive';
    const isDanger = confirmVariant === 'destructive' || confirmVariant === 'outline-destructive' || confirmVariant === 'ghost-destructive';

    return (
        <AlertDialog open={dialogState.isOpen} onOpenChange={handleOpenClose}>
            <AlertDialogContent
                ref={modalRef}
                variant={isDanger ? 'danger' : 'default'}
                onOpenAutoFocus={(e) => {
                    e.preventDefault();
                    if (e.target && 'querySelector' in e.target && typeof e.target.querySelector === 'function') {
                        e.target.querySelector('[data-autofocus]')?.focus();
                    }
                }}
            >
                <AlertDialogHeader>
                    <AlertDialogTitle>{dialogState.title}</AlertDialogTitle>
                    <AlertDialogDescription>
                        {dialogState.message}
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel>
                        {dialogState.cancelLabel ?? 'Abbrechen'}
                    </AlertDialogCancel>
                    <AlertDialogAction
                        data-autofocus
                        onKeyDown={handleConfirmKeyDown}
                        onClick={handleConfirm}
                        className={cn(buttonVariants({ variant: confirmVariant }), isDanger && 'border-fc-red/55 bg-fc-red/[0.22] text-fc-red-bright hover:bg-fc-red/[0.32]')}
                    >
                        {dialogState.actionLabel ?? 'Fortfahren'}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
